"use client";

import { useState } from "react";
import Button from "@ishvaram/ui/Button";
import Input from "@ishvaram/ui/Input";
import { BirthPlaceField } from "./BirthPlaceField";
import TrustStrip from "./TrustStrip";
import {
  useBirthDetailsFormController,
  type BirthDetailsFormProps,
  type BirthFormLabels,
} from "./BirthDetailsForm.controller";

export default function BirthDetailsForm(props: BirthDetailsFormProps) {
  const form = useBirthDetailsFormController(props);

  return (
    <form onSubmit={form.handleSubmit} className="card-sacred p-6 sm:p-8 space-y-5">
      {form.showTrustStrip && <TrustStrip language={props.language ?? "en"} showCheckout={false} />}
      <FormHeading heading={props.heading} />
      {form.collectName && (
        <NameField
          heading={props.heading}
          labels={form.labels}
          name={form.name}
          setName={(value) => form.setField("name", value)}
          optionalName={form.optionalName}
          trackStart={form.trackStart}
        />
      )}
      <DateTimeFields
        heading={props.heading}
        labels={form.labels}
        birthDate={form.birthDate}
        birthTime={form.birthTime}
        setBirthTime={(value) => form.setField("birthTime", value)}
        requireBirthTime={form.requireBirthTime}
        todayIso={form.todayIso}
        dateError={form.dateError}
        handleDateChange={form.handleDateChange}
        trackStart={form.trackStart}
      />
      <BirthPlaceField
        id={fieldId("birthPlace", props.heading)}
        labels={form.labels}
        birthPlace={form.birthPlace}
        selectedPlace={form.selectedPlace}
        setBirthPlace={(value) => form.setField("birthPlace", value)}
        setSelectedPlace={(value) => form.setField("selectedPlace", value)}
        placeIsUnresolved={form.placeIsUnresolved}
        trackStart={form.trackStart}
      />
      <FormError error={props.error ?? form.submitBlockedError} />
      <SubmitButton
        loading={props.loading}
        calculatingLabel={form.labels.calculating}
        submitLabel={form.effectiveSubmitLabel}
      />
    </form>
  );
}

function FormHeading({ heading }: { heading?: string }) {
  if (!heading) return null;
  return (
    <h3
      className="text-[16px] font-bold"
      style={{
        fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
        color: "var(--ish-dark)",
      }}
    >
      {heading}
    </h3>
  );
}

function fieldId(name: string, heading?: string): string {
  return `${name}-${heading || "default"}`;
}

function NameField({
  heading,
  labels,
  name,
  setName,
  optionalName,
  trackStart,
}: {
  heading?: string;
  labels: BirthFormLabels;
  name: string;
  setName: (value: string) => void;
  optionalName: boolean;
  trackStart: () => void;
}) {
  return (
    <Input
      id={fieldId("name", heading)}
      type="text"
      required={!optionalName}
      aria-required={optionalName ? undefined : "true"}
      label={optionalName ? `${labels.yourName} ${labels.optional}` : labels.yourName}
      value={name}
      onChangeText={(value) => {
        trackStart();
        setName(value);
      }}
      placeholder={optionalName ? labels.namePlaceholderOptional : labels.enterFullName}
      autoComplete="name"
      autoCapitalize="words"
    />
  );
}

function DateTimeFields({
  heading,
  labels,
  birthDate,
  birthTime,
  setBirthTime,
  requireBirthTime,
  todayIso,
  dateError,
  handleDateChange,
  trackStart,
}: {
  heading?: string;
  labels: BirthFormLabels;
  birthDate: string;
  birthTime: string;
  setBirthTime: (value: string) => void;
  requireBirthTime: boolean;
  todayIso: string;
  dateError?: string;
  handleDateChange: (value: string) => void;
  trackStart: () => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Input
        id={fieldId("birthDate", heading)}
        type="date"
        required
        aria-required="true"
        label={labels.birthDate}
        value={birthDate}
        onChangeText={handleDateChange}
        autoComplete="bday"
        min="1900-01-01"
        max={todayIso}
        error={dateError}
      />
      <BirthTimeField
        heading={heading}
        labels={labels}
        birthTime={birthTime}
        setBirthTime={setBirthTime}
        requireBirthTime={requireBirthTime}
        trackStart={trackStart}
      />
    </div>
  );
}

function BirthTimeField({
  heading,
  labels,
  birthTime,
  setBirthTime,
  requireBirthTime,
  trackStart,
}: {
  heading?: string;
  labels: BirthFormLabels;
  birthTime: string;
  setBirthTime: (value: string) => void;
  requireBirthTime: boolean;
  trackStart: () => void;
}) {
  return (
    <div>
      <label
        htmlFor={fieldId("birthTime", heading)}
        className="block text-[13px] font-semibold mb-2"
        style={{ color: "var(--ish-dark)" }}
      >
        {labels.birthTime}{" "}
        <span className="font-normal" style={{ color: "var(--ish-text-faint)" }}>
          {requireBirthTime ? labels.required : labels.optional}
        </span>
      </label>
      <Input
        id={fieldId("birthTime", heading)}
        type="time"
        required={requireBirthTime}
        aria-required={requireBirthTime ? "true" : undefined}
        value={birthTime}
        onChangeText={(value) => {
          trackStart();
          setBirthTime(value);
        }}
      />
      <p className="mt-1.5 text-[11px] leading-snug" style={{ color: "var(--ish-text-faint)" }}>
        {requireBirthTime ? labels.timeRequiredHint : labels.timeOptionalHint}
      </p>
      {!requireBirthTime && (
        <BirthTimeUnknownHelper
          toggleLabel={labels.timeUnknownToggle}
          explainer={labels.timeUnknownExplainer}
        />
      )}
    </div>
  );
}

function BirthTimeUnknownHelper({
  toggleLabel,
  explainer,
}: {
  toggleLabel: string;
  explainer: string;
}) {
  // Inline honesty disclosure (rule 32 dead-click + rule 22 voice): a timeless
  // chart has no Lagna/houses/dasha, so we name the noon-default consequence
  // instead of silently promising reads the chart can't support.
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-[11px] font-semibold"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          color: "var(--ish-accent, var(--color-accent))",
          textDecoration: "underline dotted",
        }}
      >
        {toggleLabel}
      </button>
      {open && (
        <p className="mt-1.5 text-[11px] leading-snug" style={{ color: "var(--ish-text-muted)" }}>
          {explainer}
        </p>
      )}
    </div>
  );
}

function FormError({ error }: { error?: string }) {
  if (!error) return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      className="text-[13px] rounded-lg p-3.5"
      style={{
        color: "var(--color-error-text)",
        backgroundColor: "var(--color-error-bg)",
        border: "1px solid var(--color-error-tint)",
      }}
    >
      {error}
    </div>
  );
}

function SubmitButton({
  loading,
  calculatingLabel,
  submitLabel,
}: {
  loading: boolean;
  calculatingLabel: string;
  submitLabel: string;
}) {
  return (
    <Button
      type="submit"
      onPress={() => {
        /* form.onSubmit handles it — this keeps Enter-key behavior */
      }}
      disabled={loading}
      loading={loading}
      variant="primary"
      title={loading ? calculatingLabel : `${submitLabel} \u2192`}
      className="w-full py-3.5"
    />
  );
}
